import { Injectable, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { AgremoAnalysisType } from '../response-models/agremo-analysis-type.model';
import { toAgremoAnalysisTypes } from '../utils/agremo-utils';
import { isRecord } from '../utils/is-record';
import { nullableBoolean } from '../utils/wire-value-utils';

/**
 * Agremo integration-level endpoints with no entity model: `GET /user/agremo/status` (is the integration
 * configured) and `GET /user/agremo/analysis-types` (hub `user-agremo-controller.ts`).
 * Maps live in `AgremoMapService`, analyses in `AgremoAnalysisService`, imagery uploads in
 * `AgremoImageryUploadService`.
 *
 * Reference: /agent-os/specs/2026-01-22-agremo-api-integration/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class AgremoService extends ApiService {
  private configuredSignal = signal<boolean | null>(null); // null = unknown (not yet checked)
  private analysisTypesSignal = signal<AgremoAnalysisType[]>([]);

  // In-flight promise for checkStatus, so concurrent callers share a single request
  private checkStatusPromise: Promise<boolean | null> | null = null;

  readonly isConfigured = computed(() => this.configuredSignal());
  readonly analysisTypes = computed(() => this.analysisTypesSignal());

  constructor() {
    super('user/agremo');
  }

  /**
   * Whether the Agremo integration is configured on the hub. Cached for the session; concurrent callers
   * share one request. Never throws: a failure resolves `false` so the UI never sticks on a spinner.
   */
  async checkStatus(): Promise<boolean | null> {
    if (this.configuredSignal() !== null) {
      return this.configuredSignal();
    }
    if (this.checkStatusPromise) {
      return this.checkStatusPromise;
    }
    this.checkStatusPromise = this.fetchStatus();
    try {
      return await this.checkStatusPromise;
    } finally {
      this.checkStatusPromise = null;
    }
  }

  /** Load the active analysis types into `analysisTypes`. Failures are logged and leave the list unchanged. */
  async loadAnalysisTypes(): Promise<void> {
    try {
      const types = await firstValueFrom(this.request('GET', { path: 'analysis-types' }, toAgremoAnalysisTypes));
      this.analysisTypesSignal.set(types);
    } catch (error: unknown) {
      console.error('Error loading analysis types:', ApiError.fromHttpError(error).message);
    }
  }

  private async fetchStatus(): Promise<boolean | null> {
    try {
      const read = (data: unknown) => (isRecord(data) ? nullableBoolean(data['configured']) ?? false : false);
      const configured = await firstValueFrom(this.request('GET', { path: 'status' }, read));
      this.configuredSignal.set(configured);
      return configured;
    } catch {
      // A page reload will re-check.
      console.warn('[AgremoService] Failed to check Agremo status, defaulting to not configured');
      this.configuredSignal.set(false);
      return false;
    }
  }
}
